import { useAuth, useTranslation } from '@/context/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, createContext, useContext } from 'react';
import { getFamilies } from '@/lib/api';
import {
  LayoutDashboard, Map, ClipboardList, Wallet, ShieldCheck, Heart,
  Settings, LogOut, Globe, Menu, X, Search, Bell, ChevronDown, Users, PanelLeftClose, PanelLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useRole } from '@/context/RoleContext';
import ModeSelector from '@/components/ModeSelector';

// Sidebar context for global collapse state
const SidebarContext = createContext({ collapsed: false, setCollapsed: () => {} });
export const useSidebar = () => useContext(SidebarContext);

export const AppLayout = ({ children }) => {
  const { user, language, switchLanguage, logout, families, setFamilies } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Get role from context
  const { role, canSwitchMode, switchMode, loading: roleLoading } = useRole();

  useEffect(() => {
    getFamilies().then(res => setFamilies(res.data)).catch(() => {});
  }, [setFamilies]);

  // Auto-collapse on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarCollapsed(true);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { to: '/trips', icon: Map, label: t('nav.trips') },
    { to: '/tasks', icon: ClipboardList, label: t('nav.tasks') },
    { to: '/budget', icon: Wallet, label: t('nav.budget') },
    { to: '/compliance', icon: ShieldCheck, label: t('nav.compliance') },
    { to: '/favorites', icon: Heart, label: language === 'fr' ? 'Mes Favoris' : 'My Favorites' },
    { to: '/settings', icon: Settings, label: t('nav.settings') },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const initials = user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';

  // Collapsed sidebar (icon-only mode)
  const CollapsedSidebar = () => (
    <div className="flex flex-col h-full gradient-airhelp items-center py-4">
      {/* Logo */}
      <Link to="/dashboard" className="mb-4" data-testid="app-logo-collapsed">
        <img 
          src="/images/logo_LP_favi.png" 
          alt="LifePulse" 
          className="w-10 h-10"
        />
      </Link>

      {/* Expand button */}
      <button 
        onClick={() => setSidebarCollapsed(false)}
        className="p-2 rounded-xl hover:bg-white/10 text-white/70 hover:text-white mb-4"
        data-testid="expand-sidebar-btn"
      >
        <PanelLeft className="w-5 h-5" />
      </button>

      {/* Nav icons */}
      <nav className="flex-1 flex flex-col items-center gap-1">
        {navItems.map(item => {
          const active = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`p-3 rounded-xl transition-all ${
                active 
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/30' 
                  : 'text-purple-200/60 hover:text-white hover:bg-white/5'
              }`}
              title={item.label}
            >
              <item.icon className="w-5 h-5" strokeWidth={1.8} />
            </Link>
          );
        })}
      </nav>

      {/* Bottom icons */}
      <div className="flex flex-col items-center gap-1 mt-auto">
        <button
          onClick={() => switchLanguage(language === 'fr' ? 'en' : 'fr')}
          className="p-3 rounded-xl text-purple-200/60 hover:text-white hover:bg-white/5"
          title={language === 'fr' ? 'English' : 'Français'}
        >
          <Globe className="w-5 h-5" />
        </button>
        <button 
          onClick={handleLogout}
          className="p-3 rounded-xl text-pink-300 hover:text-pink-200 hover:bg-pink-500/10"
          title={t('nav.logout')}
        >
          <LogOut className="w-5 h-5" />
        </button>
      </div>
    </div>
  );

  const SidebarContent = () => (
    <div className="flex flex-col h-full gradient-airhelp">
      {/* Logo + Collapse button */}
      <div className="px-5 py-5 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="app-logo">
          <img 
            src="/images/logo_LP_favi.png" 
            alt="LifePulse" 
            className="w-9 h-9"
          />
          <span className="font-bold text-white text-[15px] tracking-tight">LifePulse</span>
        </Link>
        <button 
          onClick={() => setSidebarCollapsed(true)}
          className="p-1.5 rounded-lg hover:bg-white/10 text-white/70 hover:text-white hidden lg:block"
          data-testid="collapse-sidebar-btn"
        >
          <PanelLeftClose className="w-5 h-5" />
        </button>
      </div>

      {/* User card */}
      <div className="px-4 pb-4">
        <div className="flex items-center gap-3 px-3 py-3 rounded-xl bg-white/5 border border-white/10">
          <Avatar className="w-9 h-9 border-2 border-purple-400/50">
            <AvatarImage src={user?.picture} />
            <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-xs font-bold">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
            <p className="text-[10px] text-purple-300/70 truncate">{families[0]?.name || ''}</p>
          </div>
        </div>
      </div>

      {/* Nav items */}
      <nav className="flex-1 px-3 space-y-0.5">
        {navItems.map(item => {
          const active = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                active 
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/30' 
                  : 'text-purple-200/60 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`sidebar-${item.to.replace('/', '')}`}
            >
              <item.icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
              {item.label}
            </Link>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="px-3 py-4 border-t border-white/10">
        <button
          onClick={() => switchLanguage(language === 'fr' ? 'en' : 'fr')}
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-purple-200/60 hover:text-white hover:bg-white/5 transition-all w-full"
          data-testid="lang-switch-sidebar"
        >
          <Globe className="w-[18px] h-[18px]" strokeWidth={1.8} />
          {language === 'fr' ? 'English' : 'Francais'}
        </button>
        <button 
          onClick={handleLogout} 
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-pink-300 hover:text-pink-200 hover:bg-pink-500/10 transition-all w-full" 
          data-testid="logout-sidebar"
        >
          <LogOut className="w-[18px] h-[18px]" strokeWidth={1.8} />
          {t('nav.logout')}
        </button>
      </div>
    </div>
  );

  // Calculate sidebar width based on collapsed state
  const sidebarWidth = sidebarCollapsed ? 'w-16' : 'w-60';
  const mainOffset = sidebarCollapsed ? 'md:left-16' : 'md:left-60';
  const mainPadding = sidebarCollapsed ? 'md:pl-16' : 'md:pl-60';

  return (
    <SidebarContext.Provider value={{ collapsed: sidebarCollapsed, setCollapsed: setSidebarCollapsed }}>
      <div className="min-h-screen bg-gradient-to-br from-purple-50/50 via-white to-pink-50/30">
        {/* Desktop sidebar - collapsible */}
        <aside 
          className={`hidden md:block fixed left-0 top-0 ${sidebarWidth} h-screen z-30 transition-all duration-300`} 
          data-testid="app-sidebar"
        >
          {sidebarCollapsed ? <CollapsedSidebar /> : <SidebarContent />}
        </aside>

        {/* Top bar */}
        <header className={`fixed top-0 left-0 ${mainOffset} right-0 h-14 bg-white/80 backdrop-blur-md border-b border-purple-100 z-20 flex items-center px-4 md:px-6 gap-4 transition-all duration-300`} data-testid="app-topbar">
          <button onClick={() => setSidebarOpen(true)} className="md:hidden p-1.5" data-testid="mobile-sidebar-toggle">
            <Menu className="w-5 h-5 text-purple-700" />
          </button>
          
          {/* Toggle collapse on larger screens */}
          <button 
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)} 
            className="hidden md:block p-1.5 rounded-lg hover:bg-purple-50 text-purple-600"
            data-testid="toggle-sidebar-btn"
          >
            {sidebarCollapsed ? <PanelLeft className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
          </button>

          {/* Search */}
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-400" />
            <Input
              placeholder={t('common.search') + '...'}
              className="pl-9 h-9 bg-purple-50/50 border-purple-200 rounded-xl text-sm focus:ring-purple-500/20 focus:border-purple-300"
              data-testid="global-search"
            />
          </div>

          {/* Mode Selector - Global (appears on all pages for organizers) */}
          {!roleLoading && canSwitchMode && (
            <div className="hidden lg:block">
              <ModeSelector 
                currentRole={role} 
                canSwitch={canSwitchMode} 
                onSwitchMode={switchMode}
              />
            </div>
          )}

        <div className="flex items-center gap-2">
          {/* Notifications */}
          <button className="relative p-2 rounded-xl hover:bg-purple-50 transition-colors" data-testid="notifications-btn">
            <Bell className="w-[18px] h-[18px] text-purple-600" strokeWidth={1.8} />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-pink-500 rounded-full" />
          </button>

          {/* User dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 pl-2 pr-1 py-1 rounded-xl hover:bg-purple-50 transition-colors" data-testid="user-menu-trigger">
                <Avatar className="w-7 h-7">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-[10px] font-bold">{initials}</AvatarFallback>
                </Avatar>
                <span className="hidden sm:block text-sm font-medium text-purple-900">{user?.name?.split(' ')[0]}</span>
                <ChevronDown className="w-3.5 h-3.5 text-purple-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu">
                <Settings className="w-4 h-4 mr-2" /> {t('nav.settings')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => switchLanguage(language === 'fr' ? 'en' : 'fr')} data-testid="lang-switch-menu">
                <Globe className="w-4 h-4 mr-2" /> {language === 'fr' ? 'English' : 'Francais'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-pink-600" data-testid="logout-menu-item">
                <LogOut className="w-4 h-4 mr-2" /> {t('nav.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 w-64 h-full shadow-2xl animate-slide-in-left">
            <div className="absolute top-3 right-3 z-10">
              <button onClick={() => setSidebarOpen(false)} className="p-1 text-white/60 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main content - responsive to sidebar collapse */}
      <main className={`${sidebarCollapsed ? 'md:ml-16' : 'md:ml-60'} pt-14 min-h-screen transition-all duration-300`}>
        <AlertBanner />
        <div className="p-4 md:p-6 max-w-[1400px]">
          {children}
        </div>
      </main>
    </div>
    </SidebarContext.Provider>
  );
};

function AlertBanner() {
  const [alerts, setAlerts] = useState([]);
  useEffect(() => {
    const load = async () => {
      try {
        const { getDashboardAlerts } = await import('@/lib/api');
        const res = await getDashboardAlerts();
        setAlerts(res.data);
      } catch {}
    };
    load();
  }, []);
  if (alerts.length === 0) return null;
  const incident = alerts.find(a => a.type === 'incident');
  if (!incident) return null;
  return (
    <div className="bg-red-500 text-white px-4 py-2.5 flex items-center justify-between animate-fade-in" data-testid="alert-banner">
      <div className="flex items-center gap-3 text-sm">
        <span className="font-bold">ALERTE</span>
        <span>{incident.title} — {incident.trip_name}</span>
      </div>
      <a href={`/trips/${incident.trip_id}`} className="text-xs font-semibold bg-white/20 hover:bg-white/30 rounded-lg px-3 py-1 transition-colors">
        Voir Plan B →
      </a>
    </div>
  );
}
